/**
 * Data for member accounts: the list of their groups (with their balance in each), join
 * requests and groups they could join. A group itself opens on the same screens the treasurer
 * uses, through app.openMemberGroup (one pipeline: /api/member/groups/:id/state).
 */
import type { PersonStatus } from "@split/core";
import { api, ApiError } from "./api.ts";

export interface MemberGroup {
  id: string;
  name: string;
  me: {
    name: string;
    color: string;
    totalMicro: number;
    balanceMicro: number;
    status: PersonStatus;
  } | null;
  itemCount: number;
  lastDate: string | null;
  treasurer?: string | null;
  /** "treasurer" = this account runs that club (the full app for that group). */
  role?: "member" | "treasurer";
  /** Who this account is in that group. */
  personId?: string;
}

function load<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked: fine, it just won't open offline */
  }
}

export interface JoinRequest {
  id: string;
  groupId: string;
  groupName: string;
  personId: string;
  status: "pending" | "approved" | "denied";
  createdAt: string;
}

export interface NameMatch {
  groupId: string;
  groupName: string;
  personId: string;
  name: string;
  color: string;
  taken?: boolean;
}

/** A group this account could join, with people in it who share its name. */
export interface Joinable {
  groupId: string;
  name: string;
  joining: "open" | "ask";
  matches: NameMatch[];
}

class MemberStore {
  /**
   * "member": a friend's own account. "preview": the treasurer using Member view, which shows
   * the same screens for their own person in each group (from /api/preview).
   */
  mode = $state<"member" | "preview">("member");
  private get base() {
    return this.mode === "preview" ? "/api/preview/groups" : "/api/member/groups";
  }
  private get prefix() {
    return this.mode === "preview" ? "preview:" : "member:";
  }

  groups = $state<MemberGroup[] | null>(load("member:groups"));
  /** This account's requests to join as someone, and people it might be (member mode only). */
  requests = $state<JoinRequest[]>([]);
  joinable = $state<Joinable[]>([]);
  /** True when what's shown is the saved copy because the network failed. */
  stale = $state(false);

  async refreshGroups(): Promise<void> {
    try {
      const { groups } = await api<{ groups: MemberGroup[] }>(this.base);
      this.groups = groups;
      this.stale = false;
      save(`${this.prefix}groups`, groups);
    } catch (e) {
      if (!(e instanceof ApiError)) this.stale = true;
      this.groups ??= [];
    }
  }

  async refreshRequests(): Promise<void> {
    if (this.mode !== "member") return;
    try {
      const [r, j] = await Promise.all([
        api<{ requests: JoinRequest[] }>("/api/member/requests"),
        api<{ groups: Joinable[] }>("/api/member/joinable"),
      ]);
      this.requests = r.requests;
      this.joinable = j.groups;
    } catch {
      /* offline */
    }
  }

  /** "I'm in this group": as a new person (personId null) or as someone already in it. */
  async join(groupId: string, personId: string | null): Promise<"joined" | "asked"> {
    const r = await api<{ status: "joined" | "asked" }>("/api/member/join", {
      body: { groupId, personId },
    });
    await Promise.all([this.refreshRequests(), this.refreshGroups()]);
    return r.status;
  }

  /** Switches between a member's own data and the treasurer's Member view. */
  setMode(mode: "member" | "preview") {
    if (this.mode === mode) return;
    this.mode = mode;
    this.groups = load(`${this.prefix}groups`);
  }

  clear() {
    this.groups = null;
    try {
      for (const k of Object.keys(localStorage))
        if (k.startsWith("member:") || k.startsWith("preview:")) localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

export const member = new MemberStore();
